import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './Contact.css';

const createInitialFormData = () => ({
  name: '',
  email: '',
  company: '',
  service: '',
  message: '',
  website: '',
  _formStartedAt: Date.now()
});

const locations = [
  {
    code: 'US',
    name: 'United States',
    company: 'Valley International Supply',
    address: ['1474 W. Price Rd., Ste. 107', 'Brownsville, Texas 78520']
  },
  {
    code: 'MX',
    name: 'Tamaulipas, Mexico ',
    company: 'Valley International Supply S. de R.L. de C.V.',
    address: ['Matamoros, Tamaulipas']
  },
  {
    code: 'MX',
    name: 'Monterrey, Mexico',
    company: 'Valley International Supply S. de R.L. de C.V.',
    address: ['Calz. San Pedro # 223 Col Miravalle, Monterrey, Nuevo León.']
  },
  {
    code: 'HN',
    name: 'Central America',
    company: 'Valley International Supply Centroamérica',
    address: ['San Pedro Sula, Honduras']
  }
];

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(createInitialFormData);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/send-contact-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        let message = 'We could not send your message. Please try again.';
        try {
          const data = await response.json();
          message = response.status === 429
            ? 'Too many requests. Please try again in an hour.'
            : data.error || message;
        } catch {
          // Use the friendly default message when the server does not return JSON.
        }
        throw new Error(message);
      }

      toast.success('Message sent. Our team will contact you soon.');
      setFormData(createInitialFormData());
      navigate('/thank-you?type=contact');
    } catch (error) {
      toast.error(error.message || 'We could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="va-contact">
      <div className="va-container">
        <div className="va-contact-intro">
          <div>
            <span className="va-eyebrow">Let’s solve it together</span>
            <h2>What does your<br />operation need next?</h2>
          </div>
          <div className="va-contact-intro-copy">
            <p>
              Send a part number, describe an application or share your project list.
              A member of our team will follow up with a clear next step.
            </p>
            <div>
              <a href="tel:+19563380631"><i className="fas fa-phone-alt" /> +1 956-338-0631</a>
              <a href="mailto:inquiry@valleyintlsupply.com"><i className="fas fa-envelope" /> inquiry@valleyintlsupply.com</a>
              <span><i className="fas fa-clock" /> Monday–Friday · 7:00–18:00 CST</span>
            </div>
          </div>
        </div>

        <div className="va-contact-layout">
          <form className="va-contact-form" onSubmit={handleSubmit}>
            <div className="va-honeypot" aria-hidden="true">
              <label htmlFor="contact-website">Leave this field empty</label>
              <input
                id="contact-website"
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <div className="va-form-heading">
              <span>Project inquiry</span>
              <p>Fields marked with * are required.</p>
            </div>

            <div className="va-field-grid">
              <div className="va-field">
                <label htmlFor="contact-name">Full name *</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  autoComplete="name"
                  maxLength="100"
                  required
                />
              </div>
              <div className="va-field">
                <label htmlFor="contact-email">Work email *</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  autoComplete="email"
                  maxLength="254"
                  required
                />
              </div>
              <div className="va-field">
                <label htmlFor="contact-company">Company</label>
                <input
                  id="contact-company"
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Company name"
                  autoComplete="organization"
                  maxLength="150"
                />
              </div>
              <div className="va-field">
                <label htmlFor="contact-service">How can we help?</label>
                <select
                  id="contact-service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                >
                  <option value="">Select a service</option>
                  <option value="distribution">Product sourcing</option>
                  <option value="cross-reference">Part identification or cross-reference</option>
                  <option value="assistance">Engineering selection support</option>
                  <option value="project">Project or BOM support</option>
                  <option value="fulfillment">Inventory and fulfillment</option>
                  <option value="support">Customer support</option>
                </select>
              </div>
            </div>

            <div className="va-field va-message-field">
              <label htmlFor="contact-message">Tell us about the requirement *</label>
              <textarea
                id="contact-message"
                name="message"
                rows="6"
                value={formData.message}
                onChange={handleChange}
                placeholder="Part number, brand, quantity, application or delivery requirements..."
                minLength="10"
                maxLength="4000"
                required
              />
            </div>

            <div className="va-form-footer">
              <p>By submitting this form, you agree to be contacted about your inquiry.</p>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send inquiry'}
                <span aria-hidden="true">{submitting ? '·' : '→'}</span>
              </button>
            </div>
          </form>

          <aside className="va-locations" aria-label="Our locations">
            <div className="va-locations-heading">
              <span>Where to find us</span>
              <h3>Regional teams.<br />One connected network.</h3>
            </div>
            <div className="va-location-list">
              {locations.map((location) => (
                <article key={location.code}>
                  <span className="va-country-code">{location.code}</span>
                  <div>
                    <span>{location.name}</span>
                    <h4>{location.company}</h4>
                    <p>{location.address[0]}<br />{location.address[1]}</p>
                  </div>
                </article>
              ))}
            </div>
            <a
              className="va-linkedin"
              href="https://www.linkedin.com/company/valley-international-supply/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-linkedin-in" />
              Connect with us on LinkedIn
              <span>↗</span>
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Contact;
